// Cookie consent — bottom banner + preferences modal.
//
// Premium SaaS styling — dark glassmorphism card, blue/purple gradient accents,
// 200–300ms spring animations, iOS-style toggle switches, full keyboard access.
//
// Compliance:
//   - Banner appears only when the user has NOT made a choice yet.
//   - "Reject non-essential" carries the same visual weight as "Accept all".
//   - Every non-necessary category defaults to OFF.
//   - Choices persist in localStorage and can be revisited any time via the
//     "Cookie preferences" link in every page footer.
//   - Fully keyboard-navigable; ARIA on every control.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, Shield, Sparkles } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

import { CATEGORIES, useConsent } from "./cookieConsent";

// ---------- Banner (bottom of viewport, first-visit only) --------------------
export function CookieBanner() {
  const { hasDecided, acceptAll, rejectNonEssential, openManager } = useConsent();
  if (hasDecided) return null;

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[80] px-3 pb-3 sm:px-6 sm:pb-6 pointer-events-none"
      data-testid="cookie-banner"
    >
      <div
        className="pointer-events-auto max-w-4xl mx-auto rounded-2xl border border-white/10 bg-card/80 shadow-2xl backdrop-blur-xl relative overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
      >
        {/* subtle blue→purple accent stripe */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-purple-500/60"
        />
        <div className="p-5 sm:p-6 grid gap-5 md:grid-cols-[1fr_auto] md:items-center">
          <div className="min-w-0">
            <div className="text-[11px] font-mono uppercase tracking-widest text-primary/80 mb-2">
              Cookies
            </div>
            <div className="font-display font-bold text-lg leading-snug">
              We use cookies to make CareerOS work — and better.
            </div>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Essential cookies are always on. Everything else is off until you say yes.
              You can change your mind anytime.{" "}
              <Link to="/cookies" className="underline hover:text-foreground">Learn more</Link>.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[240px]">
            <Button size="sm" onClick={acceptAll} className="w-full" data-testid="cookie-accept-all">
              Accept all
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={rejectNonEssential}
              className="w-full"
              data-testid="cookie-reject-all"
            >
              Reject non-essential
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={openManager}
              className="w-full"
              data-testid="cookie-manage"
            >
              Manage preferences
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Preferences modal (glassmorphism + gradient) ---------------------
export function CookiePreferencesModal() {
  const { managerOpen, closeManager, choices, accept, acceptAll, rejectNonEssential } = useConsent();
  const [draft, setDraft] = useState(() => ({ ...choices }));

  useEffect(() => {
    if (managerOpen) setDraft({ ...choices });
  }, [managerOpen, choices]);

  const toggle = (id) => setDraft((d) => ({ ...d, [id]: !d[id] }));
  const categories = useMemo(() => CATEGORIES, []);

  return (
    <Dialog open={managerOpen} onOpenChange={(v) => { if (!v) closeManager(); }}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden gap-0 border-white/10 bg-card/90 backdrop-blur-2xl rounded-2xl shadow-2xl"
        data-testid="cookie-modal"
      >
        {/* Gradient header accent */}
        <div
          aria-hidden
          className="h-1 w-full bg-gradient-to-r from-primary via-indigo-500 to-purple-500"
        />

        <div className="px-6 pt-6 pb-2">
          <DialogHeader>
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/25 to-purple-500/25 border border-white/10 flex items-center justify-center">
                <Shield className="w-4 h-4 text-primary" />
              </div>
              <DialogTitle className="font-display text-2xl tracking-tight">
                Cookie preferences
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm leading-relaxed">
              Choose which categories of cookies CareerOS-AI may use on your device.
              See our <Link to="/cookies" className="underline hover:text-foreground">Cookie Policy</Link> for details.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Category list */}
        <div className="px-6 pb-2 space-y-2.5 max-h-[52vh] overflow-y-auto">
          {categories.map((c) => (
            <CategoryRow
              key={c.id}
              category={c}
              checked={c.always ? true : !!draft[c.id]}
              onToggle={() => !c.always && toggle(c.id)}
            />
          ))}
        </div>

        {/* Footer buttons */}
        <div className="px-6 py-5 border-t border-white/5 mt-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 bg-black/20">
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="ghost"
              size="sm"
              onClick={rejectNonEssential}
              data-testid="cookie-modal-reject"
              className="text-muted-foreground hover:text-foreground"
            >
              Reject non-essential
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={acceptAll}
              data-testid="cookie-modal-accept-all"
            >
              Accept all
            </Button>
          </div>
          <Button
            size="sm"
            onClick={() => accept(draft)}
            data-testid="cookie-modal-save"
            className="bg-gradient-to-r from-primary to-indigo-500 hover:from-primary/90 hover:to-indigo-500/90 text-primary-foreground shadow-md"
          >
            Save preferences
          </Button>
        </div>

        {/* SR-only close (radix already provides an X in DialogContent) */}
        <button
          type="button"
          onClick={closeManager}
          className="sr-only"
          aria-label="Close cookie preferences"
        >
          <X />
        </button>
      </DialogContent>
    </Dialog>
  );
}

function CategoryRow({ category, checked, onToggle }) {
  return (
    <div
      className={`rounded-xl border transition-colors ${
        checked && !category.always
          ? "border-primary/40 bg-primary/5"
          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
      } px-4 py-3.5 flex items-start gap-4`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-display font-semibold text-sm">{category.label}</div>
          {category.always ? (
            <span className="text-[10px] font-mono uppercase tracking-widest text-primary px-1.5 py-0.5 rounded-md bg-primary/10 border border-primary/20 flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5" />
              Always active
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
          {category.description}
        </p>
      </div>
      <Switch
        checked={checked}
        disabled={!!category.always}
        onCheckedChange={onToggle}
        aria-label={`Toggle ${category.label} cookies`}
        data-testid={`cookie-toggle-${category.id}`}
        className="mt-1 shrink-0 data-[state=checked]:bg-primary data-[state=unchecked]:bg-white/10"
      />
    </div>
  );
}
